import { useState, useEffect, useMemo } from 'react';
import { codexService, CodexJobOptimization, CodexAnalysis } from '../services/codexService';

interface AIJobOptimizerProps {
  material: string;
  length: number; // in feet
  quantity: number;
  customerName?: string;
  materialUnitCost?: number; // cost per ft
  currentStock?: number; // ft available
  onOptimizationApplied?: (optimization: CodexJobOptimization) => void;
  className?: string;
}

export function AIJobOptimizer({ 
  material, 
  length, 
  quantity, 
  customerName, 
  materialUnitCost,
  currentStock,
  onOptimizationApplied,
  className = '' 
}: AIJobOptimizerProps) {
  const [optimization, setOptimization] = useState<CodexJobOptimization | null>(null);
  const [analysis, setAnalysis] = useState<CodexAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<'optimization' | 'analysis'>('optimization');
  const [isUpdating, setIsUpdating] = useState(false);

  const hasValidInputs = useMemo(() => Boolean(material) && Number(length) > 0 && Number(quantity) > 0, [material, length, quantity]);

  const meters = useMemo(() => (Number(length) > 0 ? length / 3.28084 : 0), [length]);
  const formattedFeet = useMemo(() => (Number(length) ? `${length.toFixed(2)}ft` : '0ft'), [length]);
  const formattedMeters = useMemo(() => (Number(meters) ? `${meters.toFixed(2)}m` : '0.00m'), [meters]);
  const formattedQty = useMemo(() => `${Number.isFinite(quantity as number) ? quantity : 0} pcs`, [quantity]);

  // Pricing helper (mirrors app formula)
  const calculateTotal = (unitCost: number, lenFt: number, qty: number): number => {
    const materialCost = unitCost * lenFt * qty;
    const labor = qty * 0.25;
    const waste = materialCost * 0.15;
    const subtotal = materialCost + labor + waste;
    const markup = subtotal * 0.25;
    return subtotal + markup;
  };

  const currentTotal = useMemo(() => {
    if (!materialUnitCost || !hasValidInputs) return 0;
    return calculateTotal(materialUnitCost, length, quantity);
  }, [materialUnitCost, hasValidInputs, length, quantity]);

  const currentStockRemaining = useMemo(() => {
    if (!currentStock || !hasValidInputs) return undefined;
    return currentStock - length * quantity;
  }, [currentStock, length, quantity, hasValidInputs]);

  useEffect(() => {
    if (!hasValidInputs) {
      setOptimization(null);
      setAnalysis(null);
      return;
    }
    const loadAll = async () => {
      setIsUpdating(true);
      try {
        const [opt, ana] = await Promise.all([
          codexService.optimizeJob(material, length, quantity),
          codexService.analyzeJob(material, length, quantity, customerName)
        ]);
        setOptimization(opt);
        setAnalysis(ana);
      } catch (error) {
        console.error('AI optimizer load failed', error);
      } finally {
        setIsUpdating(false);
      }
    };
    loadAll();
  }, [material, length, quantity, customerName, hasValidInputs]);

  const handleApplyOptimization = () => {
    if (optimization && onOptimizationApplied) {
      onOptimizationApplied(optimization);
    }
  };

  const optimizedTotal = useMemo(() => {
    if (!optimization || !materialUnitCost) return undefined;
    const base = currentTotal; // current price basis

    const bySavings = (typeof optimization.costSavings === 'number')
      ? Math.max(base - optimization.costSavings, 0)
      : undefined;

    const byLength = calculateTotal(materialUnitCost, optimization.suggestedLength, quantity);

    // Choose the better (lower) estimate, but never exceed current total
    let projected = base;
    if (bySavings !== undefined && byLength !== undefined) {
      projected = Math.min(bySavings, byLength, base);
    } else if (bySavings !== undefined) {
      projected = Math.min(bySavings, base);
    } else {
      projected = Math.min(byLength, base);
    }

    return projected;
  }, [optimization, materialUnitCost, quantity, currentTotal]);

  // Compact rendering helpers
  const altMaterials = optimization?.alternativeMaterials || [];
  const altVisible = altMaterials.slice(0, 2);
  const altMore = Math.max(altMaterials.length - altVisible.length, 0);

  return (
    <div className={`card-base p-4 ${className}`}>
      {/* Header (compact) */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-xl">⚡</span>
          <div>
            <h3 className="text-lg font-semibold text-black">AI Job Optimizer</h3>
            <p className="text-xs text-gray-600">Powered by Codex</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-medium outline outline-2 ${isUpdating ? 'bg-white outline-coral text-black' : 'bg-white outline-mint text-black'}`}>
            {isUpdating ? 'Updating…' : 'Live'}
          </span>
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('optimization')}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                activeTab === 'optimization' ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'
              }`}
            >
              Opt
            </button>
            <button
              onClick={() => setActiveTab('analysis')}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                activeTab === 'analysis' ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'
              }`}
            >
              Analysis
            </button>
            <button
              onClick={() => {
                setIsUpdating(true);
                Promise.all([
                  codexService.optimizeJob(material, length, quantity).then(setOptimization),
                  codexService.analyzeJob(material, length, quantity, customerName).then(setAnalysis)
                ]).finally(() => setIsUpdating(false));
              }}
              className="px-2 py-1 rounded text-xs bg-gray-200 text-black hover:bg-gray-300"
              aria-label="Refresh"
            >
              ↻
            </button>
          </div>
        </div>
      </div>

      {/* Current Inputs Summary (compact) */}
      <div className="mb-3 flex flex-wrap items-center gap-1">
        <span className="text-xs font-semibold text-black">Current:</span>
        <span className="px-1.5 py-0.5 rounded text-xs bg-white outline outline-2 outline-black">{material || '—'}</span>
        <span className="px-1.5 py-0.5 rounded text-xs bg-white outline outline-2 outline-black">{formattedFeet} ({formattedMeters})</span>
        <span className="px-1.5 py-0.5 rounded text-xs bg-white outline outline-2 outline-black">{formattedQty}</span>
      </div>

      {!hasValidInputs && (
        <div className="text-xs text-gray-600 py-2">
          Enter material, length, and quantity to see AI optimization and analysis.
        </div>
      )}

      {/* Content */}
      {hasValidInputs && activeTab === 'optimization' && optimization && (
        <div className="space-y-3">
          {/* Optimization Summary (compact) */}
          <div className="bg-mint bg-opacity-20 p-3 rounded-lg border-2 border-mint">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-black text-sm">Optimization Summary</h4>
              <span className="text-base">🎯</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-gray-600">Suggested</p>
                <p className="font-semibold text-black">{optimization.suggestedLength}ft ({(optimization.suggestedLength/3.28084).toFixed(2)}m)</p>
              </div>
              <div>
                <p className="text-gray-600">Waste ↓</p>
                <p className="font-semibold text-black">{optimization.wasteReduction}%</p>
              </div>
              <div>
                <p className="text-gray-600">Savings</p>
                <p className="font-semibold text-black">${optimization.costSavings}</p>
              </div>
            </div>
          </div>

          {/* Projected Totals & Stock Remaining (compact) */}
          {(materialUnitCost || currentStock !== undefined) && (
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-white rounded outline outline-2 outline-black">
                <p className="text-xs text-gray-600">Current Total</p>
                <p className="text-base font-semibold text-black">${currentTotal.toFixed(2)}</p>
              </div>
              {optimizedTotal !== undefined && (
                <div className="p-2 bg-white rounded outline outline-2 outline-black">
                  <p className="text-xs text-gray-600">Projected (Opt)</p>
                  <p className="text-base font-semibold text-black">${optimizedTotal.toFixed(2)}</p>
                  {optimizedTotal >= currentTotal && (
                    <p className="mt-0.5 text-[10px] text-gray-600">No savings; capped at current.</p>
                  )}
                </div>
              )}
              {currentStockRemaining !== undefined && (
                <div className="p-2 bg-white rounded outline outline-2 outline-black">
                  <p className="text-xs text-gray-600">Stock Remaining (Current)</p>
                  <p className="text-base font-semibold text-black">{currentStockRemaining.toFixed(1)} ft</p>
                </div>
              )}
              {optimizedStockRemaining !== undefined && (
                <div className="p-2 bg-white rounded outline outline-2 outline-black">
                  <p className="text-xs text-gray-600">Stock Remaining (Opt)</p>
                  <p className="text-base font-semibold text-black">{optimizedStockRemaining.toFixed(1)} ft</p>
                </div>
              )}
            </div>
          )}

          {/* Alternative Materials (show up to 2) */}
          {altVisible.length > 0 && (
            <div>
              <h4 className="font-semibold text-black mb-2 text-sm">Alternative Materials</h4>
              <div className="space-y-1.5">
                {altVisible.map((alt, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border-2 border-gray-200">
                    <div>
                      <p className="font-medium text-black text-sm">{alt.name}</p>
                      <p className="text-xs text-gray-600">Cost: ${alt.cost}/ft</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-black text-sm">${alt.savings} savings</p>
                      <button className="btn-base btn-sm bg-black text-white hover:bg-gray-800 mt-1">Select</button>
                    </div>
                  </div>
                ))}
                {altMore > 0 && (
                  <p className="text-[11px] text-gray-600">+{altMore} more alternatives available</p>
                )}
              </div>
            </div>
          )}

          <button onClick={handleApplyOptimization} className="btn-base btn-mint w-full text-sm py-2">Apply Optimization</button>
        </div>
      )}

      {hasValidInputs && activeTab === 'analysis' && analysis && (
        <div className="space-y-3">
          <div className="bg-gray-50 p-2 rounded-lg border-2 border-gray-200">
            <div className="text-xs text-black font-medium">
              Analyzing for <span className="underline decoration-2">{material}</span> • {formattedFeet} ({formattedMeters}) • {formattedQty}
            </div>
          </div>

          {analysis.materialRecommendations.length > 0 && (
            <div>
              <h4 className="font-semibold text-black mb-1 text-sm flex items-center">
                <span className="mr-2">🏗️</span>
                Material Recommendations
              </h4>
              <ul className="space-y-0.5">
                {analysis.materialRecommendations.map((rec, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start">
                    <span className="mr-1">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.pricingSuggestions.length > 0 && (
            <div>
              <h4 className="font-semibold text-black mb-1 text-sm flex items-center">
                <span className="mr-2">💰</span>
                Pricing Suggestions
              </h4>
              <ul className="space-y-0.5">
                {analysis.pricingSuggestions.map((suggestion, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start">
                    <span className="mr-1">•</span>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.safetyWarnings.length > 0 && (
            <div>
              <h4 className="font-semibold text-black mb-1 text-sm flex items-center">
                <span className="mr-2">🛡️</span>
                Safety Warnings
              </h4>
              <ul className="space-y-0.5">
                {analysis.safetyWarnings.map((warning, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start">
                    <span className="mr-1">⚠️</span>
                    {warning}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.optimizationTips.length > 0 && (
            <div>
              <h4 className="font-semibold text-black mb-1 text-sm flex items-center">
                <span className="mr-2">⚡</span>
                Optimization Tips
              </h4>
              <ul className="space-y-0.5">
                {analysis.optimizationTips.map((tip, index) => (
                  <li key={index} className="text-xs text-gray-600 flex items-start">
                    <span className="mr-1">💡</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {analysis.estimatedSavings && (
            <div className="bg-coral bg-opacity-20 p-2 rounded-lg border-2 border-coral">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-black text-sm">Estimated Savings</h4>
                  <p className="text-xs text-gray-600">Potential cost reduction</p>
                </div>
                <span className="text-xl font-bold text-black">${analysis.estimatedSavings}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
